import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import direccionClienteService from '../../services/direccionClienteService';
import { DireccionCliente } from '../../interfaces/DireccionCliente';

export interface DireccionClientePick {
  codigoDireccion: string;
  direccion: string;
}

interface DireccionClienteSearchProps {
  open: boolean;
  onClose: () => void;
  nroCliente: number;
  onSelect: (d: DireccionClientePick) => void;
}

const DireccionClienteSearch: React.FC<DireccionClienteSearchProps> = ({
  open,
  onClose,
  nroCliente,
  onSelect,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [direcciones, setDirecciones] = useState<DireccionCliente[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!nroCliente || nroCliente <= 0) {
      setDirecciones([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await direccionClienteService.getDirecciones(nroCliente);
      setDirecciones(data || []);
      if (!data || data.length === 0) {
        setError('El cliente no tiene direcciones cargadas.');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error desconocido';
      setError(`Error al cargar direcciones: ${msg}`);
      setDirecciones([]);
    } finally {
      setLoading(false);
    }
  }, [nroCliente]);

  useEffect(() => {
    if (!open) {
      setDirecciones([]);
      setError(null);
      return;
    }
    void load();
  }, [open, load]);

  const handleClose = () => {
    setDirecciones([]);
    setError(null);
    onClose();
  };

  const handleSelect = (d: DireccionCliente) => {
    onSelect({
      codigoDireccion: d.codigoDireccion || '',
      direccion: d.direccion || '',
    });
    handleClose();
  };

  const invalidCliente = !nroCliente || nroCliente <= 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isSmallScreen}
      sx={{
        '& .MuiDialog-paper': {
          margin: { xs: 0, sm: 2 },
          maxHeight: { xs: '100%', sm: '90vh' },
          height: { xs: '100%', sm: 'auto' },
        },
      }}
    >
      <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            Seleccionar dirección
          </Typography>
          <IconButton onClick={handleClose} size="small" aria-label="Cerrar">
            <CloseIcon />
          </IconButton>
        </Box>
        {!invalidCliente && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Cliente N° {nroCliente}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 2 } }}>
        {invalidCliente && (
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Indique primero un número de cliente válido para listar sus direcciones.
          </Typography>
        )}

        {!invalidCliente && loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {!invalidCliente && !loading && error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {!invalidCliente && !loading && direcciones.length > 0 && (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: isSmallScreen ? '55vh' : 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Dirección</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {direcciones.map((d) => (
                  <TableRow
                    key={`${d.nroCliente}-${d.codigoDireccion}`}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleSelect(d)}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{d.codigoDireccion}</TableCell>
                    <TableCell>{d.direccion || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!invalidCliente && !loading && direcciones.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Toque una fila para elegir la dirección.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
        {!invalidCliente && !loading && (
          <Button onClick={() => void load()} variant="outlined" size="small">
            Recargar
          </Button>
        )}
        <Button onClick={handleClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DireccionClienteSearch;
